import React, { useCallback, useEffect, useState } from "react";
import { FiCopy, FiTrash2, FiCheck, FiAlertTriangle, FiLink } from "react-icons/fi";
import ModalBase from "../Modal/ModalBase";
import { Skeleton, SkeletonGroup } from "../Skeleton";
import { ShareService, SharedConversation } from "../../services/shareService";
import { SHARE_WARNING, LIVE_SHARE_WARNING, buildShareUrl } from "../../utils/shareConversation";
import { usePostHogCapture } from "../../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../../utils/posthogEvents";

// Lists the current user's share links with toggle / copy / delete actions. Opened through ModalGlobal (modalViewsConst.manageSharedLinks, __modalSize: "md").

interface SharedLinksManagerProps {
  /** Provided by ModalGlobal — closes the modal. */
  onClose: (result?: any) => void;
}

const isMessageScope = (s: SharedConversation): boolean =>
  s.scope === "message" || !!s.messageId;

const isLiveScope = (s: SharedConversation): boolean => s.scope === "scheduleRunner";

const SharedLinksManager: React.FC<SharedLinksManagerProps> = ({ onClose }) => {
  const { capture } = usePostHogCapture();
  const [shares, setShares] = useState<SharedConversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<SharedConversation | null>(null);

  const loadShares = useCallback(async () => {
    setIsLoading(true);
    try {
      const items = await ShareService.listShares();
      setShares(items);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShares();
  }, [loadShares]);

  const handleToggle = useCallback(
    async (share: SharedConversation) => {
      const next = !share.isActive;
      setBusyId(share.id);
      try {
        await ShareService.setShareActive(share.id, next);
        setShares((prev) =>
          prev.map((s) => (s.id === share.id ? { ...s, isActive: next } : s))
        );
        capture(POSTHOG_EVENTS.SHARE_LINK_TOGGLED, { share_id: share.id, is_active: next });
      } catch (err) {
        console.error("Failed to toggle share:", err);
      } finally {
        setBusyId(null);
      }
    },
    [capture]
  );

  const handleCopy = useCallback(async (share: SharedConversation) => {
    try {
      await navigator.clipboard.writeText(buildShareUrl(share.id));
      setCopiedId(share.id);
      setTimeout(() => setCopiedId((c) => (c === share.id ? null : c)), 1500);
    } catch (err) {
      console.error("Failed to copy share link:", err);
    }
  }, []);

  const performDelete = useCallback(
    async (share: SharedConversation) => {
      setConfirmDelete(null);
      setBusyId(share.id);
      try {
        await ShareService.deleteShare(share.id);
        setShares((prev) => prev.filter((s) => s.id !== share.id));
        capture(POSTHOG_EVENTS.SHARE_LINK_DELETED, { share_id: share.id });
      } catch (err) {
        console.error("Failed to delete share:", err);
      } finally {
        setBusyId(null);
      }
    },
    [capture]
  );

  return (
    <ModalBase
      title="Manage shared links"
      icon={<FiLink className="h-5 w-5" />}
      onClose={onClose}
      size="md"
      footer={
        <button type="button" onClick={() => onClose()} className="eai-btn-outline">
          Close
        </button>
      }
    >
      {/* Warning — inline bubble, not a full-width banner. Live shares carry an ongoing grant (every future run stays public), so surface the stronger wording when any exist. */}
      <span className="inline-flex items-start gap-1.5 rounded-eai-card bg-eai-brand-peach px-3 py-1.5 font-inter text-eai-body-xs text-eai-ink ring-1 ring-eai-warning-text/25">
        <FiAlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        {shares.some(isLiveScope) ? LIVE_SHARE_WARNING : SHARE_WARNING}
      </span>

      <div className="mt-4">
        {isLoading ? (
          <SkeletonGroup count={4} label="Loading shared links" className="divide-y divide-eai-border">
            <div className="flex items-center gap-3 py-3">
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3.5 w-24" radius="full" />
              </div>
              <Skeleton className="h-5 w-9" radius="full" />
              <Skeleton className="h-eai-control-icon w-eai-control-icon" radius="full" />
              <Skeleton className="h-eai-control-icon w-eai-control-icon" radius="full" />
            </div>
          </SkeletonGroup>
        ) : shares.length === 0 ? (
          <p className="eai-description-sm py-10 text-center">
            You have not shared any conversations yet.
          </p>
        ) : (
          <ul className="divide-y divide-eai-border">
            {shares.map((share) => (
              // Wraps on a phone: the title takes the first row and the three controls the next
              <li key={share.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3">
                <div className="min-w-0 flex-1 basis-full sm:basis-0">
                  <p className="eai-card-title truncate">
                    {share.title || "Untitled conversation"}
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    <span
                      className={`eai-tag-pill-sm px-2 py-0 text-eai-caption ${
                        isLiveScope(share) ? "bg-eai-checklist-lt text-eai-cta-primary" : ""
                      }`}
                      title={isLiveScope(share) ? LIVE_SHARE_WARNING : undefined}
                    >
                      {isLiveScope(share) ? "Live agent" : isMessageScope(share) ? "Message" : "Conversation"}
                    </span>
                    {!share.isActive && (
                      <span className="font-inter text-eai-caption text-eai-ink-faint">Sharing off</span>
                    )}
                  </div>
                </div>

                {/* On/off toggle */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={share.isActive}
                  aria-label={share.isActive ? "Turn sharing off" : "Turn sharing on"}
                  disabled={busyId === share.id}
                  onClick={() => handleToggle(share)}
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                    share.isActive ? "bg-eai-cta-primary" : "bg-eai-border-hover"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      share.isActive ? "translate-x-[18px]" : "translate-x-0.5"
                    }`}
                  />
                </button>

                {/* Copy link */}
                <button
                  type="button"
                  onClick={() => handleCopy(share)}
                  aria-label="Copy share link"
                  title="Copy link"
                  className="eai-btn-icon"
                >
                  {copiedId === share.id ? (
                    <FiCheck className="h-3.5 w-3.5 text-eai-primary-accent" />
                  ) : (
                    <FiCopy className="h-3.5 w-3.5" />
                  )}
                </button>

                {/* Delete */}
                <button
                  type="button"
                  onClick={() => setConfirmDelete(share)}
                  disabled={busyId === share.id}
                  aria-label="Delete share link"
                  title="Delete"
                  className="eai-btn-icon eai-btn-icon-danger"
                >
                  <FiTrash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Delete confirmation. Fixed and above ModalGlobal's Dialog (z-[10050]) rather than nested in the body, which the panel's overflow-hidden would clip. */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[10055] flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Cancel"
            className="absolute inset-0 bg-black/40"
            onClick={() => setConfirmDelete(null)}
          />
          <div
            role="alertdialog"
            aria-modal="true"
            aria-label="Delete share link"
            className="eai-card relative w-full max-w-sm p-5"
          >
            <div className="mb-3 flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-eai-danger-surface-active text-eai-danger-text">
                <FiAlertTriangle className="h-5 w-5" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="eai-panel-title">Delete share link?</p>
                <p className="eai-description-sm mt-1">
                  Anyone with this link will no longer be able to view{" "}
                  <span className="font-medium text-eai-ink-soft">
                    {confirmDelete.title || "this conversation"}
                  </span>
                  . This can&apos;t be undone.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setConfirmDelete(null)} className="eai-btn-outline">
                Cancel
              </button>
              <button type="button" onClick={() => performDelete(confirmDelete)} className="eai-btn-danger">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalBase>
  );
};

export default SharedLinksManager;
